/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, Inject, Input,
} from '@angular/core';
import { LikeService } from '../../../../services/like.service';
import { ActionState } from './pending';
import { itemContext } from '../community-item';

// Like / unlike for a feed card ("outline trace" animation from COMMUNITY_ANIMATIONS.html, option 1C).
@Component({
  selector: 'ut-community-like-action',
  templateUrl: './community-like-action.component.html',
  styleUrls: ['./community-like-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityLikeActionComponent {
  @Input({ required: true }) likeable: any;

  readonly state = new ActionState();

  constructor(
    @Inject('$analytics') private $analytics: any,
    @Inject('$scope') private $scope: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
    private likeService: LikeService,
    private el: ElementRef<HTMLElement>,
  ) {}

  get liked(): boolean {
    return !!this.likeable.likes?.liked_by_current_user;
  }

  // The visible word plus the card it acts on; the state is in the word, so no aria-pressed as well.
  get label(): string {
    return `${this.liked ? 'Liked' : 'Like'}: ${itemContext(this.likeable)}`;
  }

  toggle(): void {
    if (this.state.busy() || this.LightTesterErrorService.show()) return;
    if (!this.likeable.current_user_permissions?.like) return;

    const like = !this.liked;
    const { type, id } = this.likeable;
    this.$analytics.eventTrack(like ? 'like' : 'unlike', { category: 'Likes', label: type });
    this.state.run(like ? this.likeService.like(type, id) : this.likeService.unlike(type, id)).then((res) => {
      this.likeable.likes.count = res.data.likes.count;
      this.likeable.likes.liked_by_current_user = res.data.likes.liked_by_current_user;
      this.state.complete(like ? 'on' : 'off', like ? 'Liked' : 'Like removed');
      this.likeService.changed.next(this.likeable);
      // For the AngularJS listeners (the Community page's trending counts); remove with the legacy pages.
      this.$scope.$emit('feedItemLiked', this.likeable);
    }, () => {
      this.AlertService.addDanger('Request failed');
      this.el.nativeElement.animate(
        [{ transform: 'translateX(0)' }, { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' }, { transform: 'none' }],
        { duration: 300 },
      );
    });
  }
}
